import { withLoading } from 'src/lib/utils';
import { assertNever } from 'src/replicant/utils';
import ResponsiveScene from '../scenes/ResponsiveScene';
import Screen from './Screen';
import Match2Scene from '../scenes/Match2Scene';
import Match2EditorScene from '../scenes/Match2EditorScene';
import { Match2PowerupType } from 'src/replicant/match2/match2.ruleset';
import { Feature } from 'src/replicant/analytics';
import Match2PlaytestScene from '../scenes/Match2PlaytestScene';
import RescueLevelScene, {
  RescuePuzzleFinishArgs,
} from '../scenes/RescueLevelScene';
import DreamDriftScene from '../scenes/DreamDriftScene';
import type { DreamDriftHero } from 'src/replicant/dreamDrift/dreamDrift.ruleset';

type Args = [
  | {
      minigame: 'match2-editor';
    }
  | {
      minigame: 'match2-playtest';
      levelNumber: number;
      selectedPowerups: Match2PowerupType[];
      bonusPowerups: Match2PowerupType[];
    }
  | {
      minigame: 'match2';
      levelNumber: number;
      selectedPowerups: Match2PowerupType[];
      bonusPowerups: Match2PowerupType[];
    }
  | {
      minigame: 'rescue-level';
      levelData: string;
      timeout: number;
      onPuzzleStart?: () => void;
      onPuzzleFinish?: (args: RescuePuzzleFinishArgs) => void;
    }
  | {
      minigame: 'dream-drift';
      levelNumber: number;
      hero: DreamDriftHero;
    },
];

type SceneType = Args[0]['minigame'];

export default class MinigameScreen extends Screen<Args> {
  private scene!: ResponsiveScene;
  private cache: Map<SceneType, ResponsiveScene> = new Map();

  /**
   * Get current scene
   */
  public get currentScene() {
    return this.scene;
  }

  /**
   * Preload minigame specific resources
   */
  public async preloadScreen(...[args]: Args) {
    const scene = this.getScene(args.minigame);

    // Configure Dream Drift before loading its level-specific assets.
    if (scene instanceof DreamDriftScene && args.minigame === 'dream-drift') {
      scene.levelNumber = args.levelNumber;
      scene.hero = args.hero;
    }

    // Early setup for rescue level, to get the level data ready for preload
    if (scene instanceof RescueLevelScene && args.minigame === 'rescue-level') {
      scene.setup(args);
    }

    if (scene) {
      await withLoading(() => scene.preload(), {
        feature: Feature.MINIGAME._,
        subFeature: Feature.MINIGAME.SCENE_PRELOAD,
      });
    }
  }

  /**
   * Show minigame scene
   */
  public showScreen(...[args]: Args) {
    this.popAll();
    const { minigame } = args;
    this.scene = this.getScene(minigame)!;

    const isPuzzleType =
      minigame === 'match2' || minigame === 'match2-playtest';

    if (this.scene instanceof Match2Scene && isPuzzleType) {
      this.scene.levelNumber = args.levelNumber;
      this.scene.selectedPowerups = args.selectedPowerups;
      this.scene.bonusPowerups = args.bonusPowerups;
    }

    if (this.scene instanceof RescueLevelScene && minigame === 'rescue-level') {
      this.scene.setup(args);
    }

    this.push(this.scene.getView());
  }

  /**
   * Create and return scene
   */
  private getScene(minigame: SceneType) {
    if (!this.cache.has(minigame)) {
      switch (minigame) {
        case 'match2':
          this.cache.set(minigame, new Match2Scene());
          break;
        case 'match2-editor':
          this.cache.set(minigame, new Match2EditorScene());
          break;
        case 'match2-playtest':
          this.cache.set(minigame, new Match2PlaytestScene());
          break;
        case 'rescue-level':
          this.cache.set(minigame, new RescueLevelScene());
          break;
        case 'dream-drift':
          this.cache.set(minigame, new DreamDriftScene());
          break;
        default:
          assertNever(minigame);
      }
    }

    return this.cache.get(minigame);
  }
}
